import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useOrderDetailContext } from 'providers/OrderDetailProvider';
import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';
import OrderLineExpandedRow from 'components/add_order/order/OrderDetail/OrderLines/OrderLineExpandedRow';
import OrderCancelModal from 'components/add_order/order/OrderDetail/OrderLines/OrderCancelModal';

import { orderApi } from 'api';
import { getOrderDetailItemsColumns } from 'constants/order';

const OrderLines = ({ orderId, onOrderUpdate }) => {
  const { orderDetailData } = useOrderDetailContext();
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const order_cancel_sync_enable = orderDetailData?.order_cancel_sync_enable;

  const tableRef = useRef(null);

  const [orderLines, setOrderLines] = useState([]);
  const [channelId, setChannelId] = useState(null);
  const [showOrderCancelModal, setShowOrderCancelModal] = useState(false);

  const refreshOrderData = useCallback(() => {
    tableRef.current?.refetch();
    onOrderUpdate?.();
  }, [onOrderUpdate]);

  const handleOpenCancelModal = async () => {
    try {
      const response = await orderApi.getOrderLines(orderId);

      setOrderLines(response || []);

      // Extract channelId from the first order line
      if (response && response.length > 0) {
        const firstLine = response[0];
        // Try to get channel_id from the order line data
        const extractedChannelId =
          firstLine.channel_id || firstLine.channel?.id;
        setChannelId(extractedChannelId);
      }

      setShowOrderCancelModal(true);
    } catch (error) {
      toast.error('Failed to fetch order data');
    }
  };

  const handleCancelOrders = async (
    selectedLineIds,
    refund,
    isRejectNotFound,
  ) => {
    try {
      await orderApi.cancelOrders(
        orderId,
        selectedLineIds,
        refund,
        isRejectNotFound,
      );

      toast.success('Successfully canceled orders');
      refreshOrderData();
    } catch (error) {
      toast.error('Failed to cancel orders');
    }
  };

  const handleOrderConnect = async () => {
    try {
      const response = await orderApi.connectOrders(orderId);

      if (response?.order_status === 'unfulfillable') {
        toast.error('Please check the product information');
      } else if (response?.order_status === 'processing') {
        toast.success('Order successfully connected');
        refreshOrderData();
      }
    } catch (error) {
      toast.error('Failed to connect order');
    }
  };

  const headerActions = (
    <>
      {orderDetailData?.orderStatus === 'unfulfillable' && (
        <Button
          variant="success"
          size="sm"
          className="text-white"
          onClick={handleOrderConnect}
        >
          Connect
        </Button>
      )}

      <Button variant="danger" size="sm" onClick={handleOpenCancelModal}>
        Cancel Orders
      </Button>
    </>
  );

  const columns = useMemo(
    () => getOrderDetailItemsColumns(tenant, warehouse),
    [tenant, warehouse],
  );

  return (
    <>
      <Table
        ref={tableRef}
        title={`Order #${orderDetailData?.channelOrderId} Items`}
        apiService={orderApi}
        columns={columns}
        fetchConfig={{
          method: 'getOrderLines',
          transformResponse: response => ({
            data: response || [],
            totalItems: response?.length || 0,
          }),
        }}
        resourceId={orderId}
        enablePagination={false}
        renderExpandedRow={row => (
          <OrderLineExpandedRow shipmentLine={row.original.shipment_lines} />
        )}
        headerActions={headerActions}
      />

      <OrderCancelModal
        show={showOrderCancelModal}
        onHide={() => setShowOrderCancelModal(false)}
        orderLines={orderLines}
        channelId={channelId}
        warehouseId={selectedWarehouse?.id}
        orderCancelSyncEnabled={order_cancel_sync_enable}
        onConfirm={handleCancelOrders}
      />
    </>
  );
};

export default OrderLines;
